/**
 * PricingTable — N-tier pricing comparison grid.
 *
 * Consumes the canonical `.tier-card`, `.tier-card-*`, `.tier-grid`
 * classes from @puffin-core/shared-styles (cards.css). Per-tier CTA
 * renders through `<SmartLink>` (LIFE-SHORTLISTED-001 F-P11-2), like MarketingHero's and CTASection's:
 * a tier's button may lead off the site (a tenant's shop), which a router `<Link to>` never loaded;
 * `onOutboundClick` reports that click.
 *
 * SEO: every consumer SHOULD pair this with `<MarketingSeo schemas={[..., 'product']}>`
 * so the schemaTemplate (Phase E) emits a `Product` JSON-LD blob per tier.
 *
 * @module @puffin-core/marketing-shell/PricingTable
 */

import PropTypes from 'prop-types';
// The SUBPATH, never the page-shell barrel (PERF-FECSS-002): this table renders on nine prerendered sites.
import { StatusBadgeCell } from '@puffin-core/page-shell/status-badge-cell';

import { SmartLink } from '../links/SmartLink.jsx';

import { SectionHeader } from './SectionHeader.jsx';

/**
 * @typedef {Object} TierFeature
 * @property {string|number} key
 * @property {string} text
 */

/**
 * @typedef {Object} TierData
 * @property {string|number} key
 * @property {string} name                Tier name (e.g. "Starter", "Pro").
 * @property {string} [tagline]           Short positioning line.
 * @property {string} [currency='$']      Currency symbol prefix.
 * @property {string} amount              Headline price (e.g. "29").
 * @property {string} [period='/mo']      Billing-period suffix.
 * @property {string} [billing]           Secondary billing line (e.g. "billed annually").
 * @property {string} [note]              Small print under the price.
 * @property {readonly TierFeature[]} features
 * @property {{label, to}} [cta]          Tier CTA; absent for a tier nobody can order yet (an anchor marked
 *                                         "coming soon", APP-ENTITLEMENT-001 Phase 11).
 * @property {{label: string, tone: string}} [badge]  A status pill in the header (the ONE tone pill,
 *                                         `<StatusBadgeCell>`): "Coming soon", "Most popular".
 * @property {boolean} [featured=false]   Visually emphasizes this tier.
 */

function CheckIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="tier-card-check">
      <path
        fillRule="evenodd"
        d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42L8.5 12.08l6.79-6.79a1 1 0 011.42 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function TierCard({ tier, onOutboundClick }) {
  const cardClass = tier.featured ? 'tier-card tier-card-featured' : 'tier-card';

  // The optional lines of the card header, resolved up front.
  const badge = tier.badge ? (
    <StatusBadgeCell
      label={tier.badge.label}
      tone={tier.badge.tone}
      testId={`tier-card-${tier.key}-badge`}
    />
  ) : null;
  const tagline = tier.tagline ? <p className="tier-card-tagline">{tier.tagline}</p> : null;
  const billing = tier.billing ? <p className="tier-card-billing">{tier.billing}</p> : null;
  const note = tier.note ? <p className="tier-card-note">{tier.note}</p> : null;

  return (
    <article className={cardClass} data-testid={`tier-card-${tier.key}`}>
      <header className="tier-card-header">
        <h3 className="tier-card-name">{tier.name}</h3>
        {badge}
        {tagline}
        <div className="tier-card-price">
          <span className="tier-card-currency">{tier.currency ?? '$'}</span>
          <span className="tier-card-amount">{tier.amount}</span>
          <span className="tier-card-period">{tier.period ?? '/mo'}</span>
        </div>
        {billing}
        {note}
      </header>
      <div className="tier-card-body">
        <ul className="tier-card-features">
          {tier.features.map((feature) => (
            <li key={feature.key} className="tier-card-feature">
              <CheckIcon />
              <span>{feature.text}</span>
            </li>
          ))}
        </ul>
      </div>
      {tier.cta ? (
        <footer className="tier-card-footer">
          <SmartLink
            href={tier.cta.to}
            onOutboundClick={onOutboundClick}
            className="btn btn-marketing-lg btn-marketing-primary"
          >
            {tier.cta.label}
          </SmartLink>
        </footer>
      ) : null}
    </article>
  );
}

TierCard.propTypes = {
  tier: PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    tagline: PropTypes.string,
    currency: PropTypes.string,
    amount: PropTypes.string.isRequired,
    period: PropTypes.string,
    billing: PropTypes.string,
    note: PropTypes.string,
    features: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        text: PropTypes.string.isRequired,
      }),
    ).isRequired,
    cta: PropTypes.shape({
      label: PropTypes.string.isRequired,
      to: PropTypes.string.isRequired,
    }),
    badge: PropTypes.shape({
      label: PropTypes.string.isRequired,
      tone: PropTypes.string.isRequired,
    }),
    featured: PropTypes.bool,
  }).isRequired,
  onOutboundClick: PropTypes.func,
};

/**
 * @param {Object} props
 * @param {string} [props.eyebrow]
 * @param {string} [props.heading]
 * @param {string} [props.subheading]
 * @param {readonly TierData[]} props.tiers
 * @param {string} [props.background] 'default' | 'alt'.
 * @param {string} [props.id]
 * @param {(href: string, destinationKind: string) => void} [props.onOutboundClick] A click on a tier
 *   button that leaves the site.
 */
export default function PricingTable({
  eyebrow,
  heading,
  subheading,
  tiers,
  background = 'default',
  id,
  onOutboundClick,
}) {
  const sectionClass = `section-marketing${background === 'alt' ? ' section-light' : ''}`;
  const sectionProps = {
    className: sectionClass,
    ...(id ? { id } : {}),
    ...(heading ? { 'aria-labelledby': `${id || 'pricing'}-heading` } : {}),
  };

  // Resolved ahead of the return so the returned tree is the section's
  // structure: heading group, then the tier grid.
  const headingGroup = (
    <SectionHeader
      eyebrow={eyebrow}
      heading={heading}
      subheading={subheading}
      headingId={`${id || 'pricing'}-heading`}
    />
  );

  return (
    <section {...sectionProps}>
      {headingGroup}
      <div className="tier-grid">
        {tiers.map((tier) => (
          <TierCard key={tier.key} tier={tier} onOutboundClick={onOutboundClick} />
        ))}
      </div>
    </section>
  );
}

PricingTable.propTypes = {
  eyebrow: PropTypes.string,
  heading: PropTypes.string,
  subheading: PropTypes.string,
  tiers: PropTypes.arrayOf(TierCard.propTypes.tier).isRequired,
  background: PropTypes.oneOf(['default', 'alt']),
  id: PropTypes.string,
  onOutboundClick: PropTypes.func,
};
